import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { AgentTaskProgress, FencyProvider } from "@fencyai/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContractAutofillContextDto, useGetContractAutofillContextQuery } from "../../autogen/bff-api";
import { fencyPromise } from "../fency/fencySetup";
import { useFency } from "../fency/useFency";
import { TipTap } from "../input/TipTap/TipTap";
import { getMarkdownFromTiptapContent, getTiptapJsonFromMarkdown } from "../input/TipTap/markdownUtils";
import { useAutofill } from "./useAutofill";

type AutofillModalContentProps = {
  autofillContext?: ContractAutofillContextDto;
  isLoadingContext: boolean;
  contextError: unknown;
  documentContent: string;
  documentMarkdown: string;
  onClose: () => void;
  onApplyContent: (content: string) => void | Promise<void>;
  onBusyChange: (isBusy: boolean) => void;
  onRegisterClose: (close: () => void) => void;
};

const AutofillModalContent = ({
  autofillContext,
  isLoadingContext,
  contextError,
  documentContent,
  documentMarkdown,
  onClose,
  onApplyContent,
  onBusyChange,
  onRegisterClose,
}: AutofillModalContentProps) => {
  const { t } = useTranslation();
  const { fetchCreateStreamingChatCompletionClientToken, fetchCreateStructuredChatCompletionClientToken } = useFency();
  const {
    generatePlan,
    applyPlan,
    planMarkdown,
    filledDocumentMarkdown,
    filledDocumentVersion,
    isGeneratingPlan,
    isApplyingPlan,
    hasError,
    latestAgentTask,
    reset,
  } = useAutofill({
    fetchCreateStreamingChatCompletionClientToken,
    fetchCreateStructuredChatCompletionClientToken,
  });

  const planTiptapContent = useMemo(
    () => (planMarkdown ? getTiptapJsonFromMarkdown(planMarkdown) : ""),
    [planMarkdown]
  );
  const [editedPlanContent, setEditedPlanContent] = useState<string | null>(null);
  const [docEditorContent, setDocEditorContent] = useState(documentContent);

  const filledTiptapContent = useMemo(
    () => (filledDocumentMarkdown ? getTiptapJsonFromMarkdown(filledDocumentMarkdown) : null),
    [filledDocumentMarkdown]
  );

  useEffect(() => {
    setEditedPlanContent(null);
  }, [planMarkdown]);

  useEffect(() => {
    if (filledTiptapContent) {
      setDocEditorContent(filledTiptapContent);
    }
  }, [filledTiptapContent]);

  const previewEditorContent = filledTiptapContent ?? docEditorContent;

  const handleGeneratePlan = useCallback(() => {
    if (!autofillContext) return;
    void generatePlan(documentMarkdown, autofillContext);
  }, [autofillContext, documentMarkdown, generatePlan]);

  const handleApplyPlan = useCallback(() => {
    const planMd = getMarkdownFromTiptapContent(editedPlanContent ?? planTiptapContent);
    const currentDocumentMarkdown = getMarkdownFromTiptapContent(docEditorContent);
    void applyPlan(currentDocumentMarkdown, planMd);
  }, [applyPlan, docEditorContent, editedPlanContent, planTiptapContent]);

  const handleClose = useCallback(() => {
    reset();
    void onApplyContent(docEditorContent);
    onClose();
  }, [docEditorContent, onApplyContent, onClose, reset]);

  const isBusy = isGeneratingPlan || isApplyingPlan;

  useEffect(() => {
    onBusyChange(isBusy);
  }, [isBusy, onBusyChange]);

  useEffect(() => {
    onRegisterClose(handleClose);
  }, [handleClose, onRegisterClose]);

  return (
    <>
      {/* Left column: Fill plan */}
      <Flex
        flex={1}
        flexDirection="column"
        minW={0}
        overflowY="hidden"
        borderRightWidth="1px"
        borderColor="smBorder"
        pr="4"
      >
        <Text fontWeight="semibold" mb="3">
          {t("Fill plan")}
        </Text>

        {isLoadingContext && (
          <Flex align="center" gap="2" color="smMuted">
            <Spinner size="sm" />
            <Text fontSize="sm">{t("Loading contract data…")}</Text>
          </Flex>
        )}

        {contextError != null && (
          <Text color="smError" fontSize="sm">
            {t("Failed to load contract data for autofill.")}
          </Text>
        )}

        {!planMarkdown && !isGeneratingPlan && !isApplyingPlan && (
          <Flex flexDirection="column" align="center" justify="center" grow="1" gap="4" py="8">
            <Text color="smMuted" textAlign="center" maxW="md">
              {t(
                "Generate a fill plan based on contract and organization data. You can review and edit the plan before applying it to the document."
              )}
            </Text>
            <Button
              colorScheme="teal"
              size="lg"
              onClick={handleGeneratePlan}
              isDisabled={!autofillContext || isLoadingContext}
            >
              {t("Generate fill plan")}
            </Button>
          </Flex>
        )}

        {isGeneratingPlan && (
          <Flex flexDirection="column" grow="1" overflow="auto">
            {latestAgentTask ? (
              <AgentTaskProgress agentTask={latestAgentTask} />
            ) : (
              <Flex align="center" gap="2" color="smMuted">
                <Spinner size="sm" />
                <Text fontSize="sm">{t("Generating fill plan…")}</Text>
              </Flex>
            )}
          </Flex>
        )}

        {planMarkdown && !isApplyingPlan && (
          <Flex flexDirection="column" grow="1" overflow="hidden" gap="3">
            <Flex flexDirection="column" grow="1" overflow="hidden">
              <TipTap
                content={editedPlanContent ?? planTiptapContent}
                editable={true}
                onUpdate={setEditedPlanContent}
                debounceWait={0}
                maxHeight="full"
                toolbar={{ template: false, autofill: false, image: false }}
              />
            </Flex>
            <Button colorScheme="teal" onClick={handleApplyPlan} alignSelf="flex-start">
              {t("Apply to document")}
            </Button>
          </Flex>
        )}

        {isApplyingPlan && (
          <Flex grow="1" align="center" justify="center">
            <Flex flexDirection="column" align="center" gap="3">
              <Spinner size="lg" color="teal.500" />
              <Text fontSize="sm" color="smMuted">
                {t("Applying fill plan to document…")}
              </Text>
            </Flex>
          </Flex>
        )}

        {hasError && (
          <Text color="smError" fontSize="sm" mt="2">
            {t("Something went wrong during autofill. Please try again.")}
          </Text>
        )}
      </Flex>

      {/* Right column: Document */}
      <Flex
        flex={1}
        flexDirection="column"
        minW={0}
        overflowY="hidden"
      >
        <Text fontWeight="semibold" mb="3">
          {t("Document preview")}
        </Text>
        {isApplyingPlan ? (
          <Flex flex={1} align="center" justify="center">
            <Flex flexDirection="column" align="center" gap="3">
              <Spinner size="lg" color="teal.500" />
              <Text fontSize="sm" color="smMuted">
                {t("Applying fill plan to document…")}
              </Text>
            </Flex>
          </Flex>
        ) : (
          <Flex flexDirection="column" grow="1" overflow="hidden" gap="2">
            <Flex flexDirection="column" grow="1" overflow="hidden">
              <TipTap
                key={filledDocumentVersion > 0 ? `filled-${filledDocumentVersion}` : "preview"}
                content={previewEditorContent}
                editable={true}
                onUpdate={setDocEditorContent}
                debounceWait={0}
                maxHeight="full"
                toolbar={{ template: false, autofill: false, image: false }}
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </>
  );
};

export const AutofillModal = ({
  contractId,
  documentContent,
  onClose,
  onApplyContent,
}: {
  contractId: string;
  documentContent: string;
  onClose: () => void;
  onApplyContent: (content: string) => void | Promise<void>;
}) => {
  const { t } = useTranslation();
  const { fetchCreateStreamClientToken } = useFency();
  const [isBusy, setIsBusy] = useState(false);
  const handleCloseRef = useRef(onClose);
  const registerClose = useCallback((close: () => void) => {
    handleCloseRef.current = close;
  }, []);
  const handleClose = useCallback(() => {
    handleCloseRef.current();
  }, []);
  const {
    data: autofillContext,
    isLoading: isLoadingContext,
    error: contextError,
  } = useGetContractAutofillContextQuery({ contractId });

  const documentMarkdown = useMemo(() => getMarkdownFromTiptapContent(documentContent), [documentContent]);

  return (
    <Modal isOpen={true} onClose={handleClose} size="full">
      <ModalOverlay />
      <ModalContent maxW="95vw" height="95vh" maxHeight="95vh" overflow="hidden">
        <ModalCloseButton right="4" />
        <ModalBody width="full" height="full" maxHeight="full">
          <Flex
            justifyContent="start"
            flexDirection="column"
            height="full"
            maxHeight="full"
            overflowY="hidden"
            width="full"
            pt="4"
            pb="6"
          >
            <Text fontWeight="semibold" fontSize="lg" pb="3">
              {t("Autofill document")}
            </Text>
            <FencyProvider fency={fencyPromise} fetchCreateStreamClientToken={fetchCreateStreamClientToken}>
              <Flex flexDirection="row" gap="4" grow="1" overflow="hidden">
                <AutofillModalContent
                  autofillContext={autofillContext}
                  isLoadingContext={isLoadingContext}
                  contextError={contextError}
                  documentContent={documentContent}
                  documentMarkdown={documentMarkdown}
                  onClose={onClose}
                  onApplyContent={onApplyContent}
                  onBusyChange={setIsBusy}
                  onRegisterClose={registerClose}
                />
              </Flex>
            </FencyProvider>
            <Flex pt="3" justifyContent="flex-end">
              <Button variant="outline" onClick={handleClose} isDisabled={isBusy}>
                {t("Close")}
              </Button>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
